import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useLocale, useT, withLocale, LOCALES, LOCALE_LABEL, type Locale } from '../i18n';
import { useTheme } from './ThemeProvider';
import { contentApi, type Solution, type Industry } from '../api/content';

const STATIC_NAV_KEYS = ['platform', 'ai_lab', 'national', 'about', 'insights', 'contact'] as const;
const STATIC_NAV_HREF: Record<(typeof STATIC_NAV_KEYS)[number], string> = {
  platform: '/platform',
  ai_lab: '/ai-lab',
  national: '/national-transformation',
  about: '/about',
  insights: '/insights',
  contact: '/contact',
};

export default function Header() {
  const locale = useLocale();
  const t = useT();
  const { theme, toggleTheme } = useTheme();
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMega, setOpenMega] = useState<'solutions' | 'industries' | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    contentApi.solutions(locale).then(setSolutions).catch(() => setSolutions([]));
    contentApi.industries(locale).then(setIndustries).catch(() => setIndustries([]));
  }, [locale]);

  useEffect(() => {
    document.body.classList.toggle('drawer-open', drawerOpen);
    return () => document.body.classList.remove('drawer-open');
  }, [drawerOpen]);

  return (
    <>
      <a className="skip-link" href="#main">
        {t('a11y.skip_to_content')}
      </a>
      <header className="site-header" id="siteHeader" ref={headerRef}>
        <div className="container nav-row">
          <Link to={withLocale('/', locale)} className="brand">
            <img src="/assets/img/cyrkil-mark.png" alt="CYRKIL" className="brand-mark-img" />
            <span>
              CYRKIL
              <small>Consulting · Engineering · Platform · AI Lab</small>
            </span>
          </Link>

          <ul className="primary-nav" id="primaryNav">
            <li className={`nav-item ${openMega === 'solutions' ? 'is-open' : ''}`} onMouseEnter={() => setOpenMega('solutions')} onMouseLeave={() => setOpenMega(null)}>
              <button className="nav-link" aria-expanded={openMega === 'solutions'} aria-haspopup="true" onClick={() => setOpenMega((v) => (v === 'solutions' ? null : 'solutions'))}>
                {t('nav.solutions')} <span className="chev">▾</span>
              </button>
              {openMega === 'solutions' && (
                <div className="mega-panel" role="menu">
                  <div className="mega-list">
                    {solutions.map((s) => (
                      <Link key={s.code} to={withLocale(`/solutions/${s.slug}`, locale)}>
                        <span className="code">{s.code}</span>
                        {s.name}
                      </Link>
                    ))}
                    <Link to={withLocale('/solutions', locale)} className="mega-cta">
                      {t('nav.view_all_12')}
                    </Link>
                  </div>
                  <div className="mega-preview">
                    <h4>{t('nav.mega_solutions_title')}</h4>
                    <p>{t('nav.mega_solutions_body')}</p>
                    <Link className="mega-cta" to={withLocale('/methodology', locale)}>
                      {t('nav.mega_solutions_cta')}
                    </Link>
                  </div>
                </div>
              )}
            </li>
            <li className={`nav-item ${openMega === 'industries' ? 'is-open' : ''}`} onMouseEnter={() => setOpenMega('industries')} onMouseLeave={() => setOpenMega(null)}>
              <button className="nav-link" aria-expanded={openMega === 'industries'} aria-haspopup="true" onClick={() => setOpenMega((v) => (v === 'industries' ? null : 'industries'))}>
                {t('nav.industries')} <span className="chev">▾</span>
              </button>
              {openMega === 'industries' && (
                <div className="mega-panel" role="menu">
                  <div className="mega-list">
                    {industries.map((i) => (
                      <Link key={i.slug} to={withLocale(`/industries/${i.slug}`, locale)}>
                        {i.name}
                      </Link>
                    ))}
                  </div>
                  <div className="mega-preview">
                    <h4>{t('nav.mega_industries_title')}</h4>
                    <p>{t('nav.mega_industries_body')}</p>
                    <Link className="mega-cta" to={withLocale('/industries', locale)}>
                      {t('nav.view_all_10')}
                    </Link>
                  </div>
                </div>
              )}
            </li>
            {STATIC_NAV_KEYS.map((key) => (
              <li className="nav-item" key={key}>
                <NavLink className={({ isActive }) => `nav-link ${isActive ? 'is-active' : ''}`} to={withLocale(STATIC_NAV_HREF[key], locale)}>
                  {t(`nav.${key}`)}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="nav-spacer" />
          <div className="nav-actions">
            <div className="lang-switch" role="group" aria-label="Language">
              {LOCALES.map((l) => (
                <LangPill key={l} lang={l} current={locale} />
              ))}
            </div>
            <button className="icon-btn" id="themeToggle" aria-label={t('nav.toggle_theme')} title={t('nav.toggle_theme')} onClick={toggleTheme}>
              {theme === 'light' ? '◑' : '◐'}
            </button>
            <Link className="icon-btn" to={withLocale('/portal-login', locale)} aria-label={t('nav.portal')} title={t('nav.portal')}>
              ⇥
            </Link>
            <Link className="btn btn-primary btn-sm" to={withLocale('/contact', locale) + '?purpose=start_transformation'}>
              {t('nav.start_transformation')}
            </Link>
            <button className="icon-btn menu-toggle" id="menuToggle" aria-label={t('nav.open_menu')} aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
              ☰
            </button>
          </div>
        </div>
      </header>

      <div className={`drawer-scrim ${drawerOpen ? 'is-open' : ''}`} id="drawerScrim" onClick={() => setDrawerOpen(false)} />
      <nav className={`mobile-drawer ${drawerOpen ? 'is-open' : ''}`} id="mobileDrawer" aria-label="Mobile navigation">
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <strong>{t('nav.menu')}</strong>
          <button className="icon-btn" id="drawerClose" aria-label={t('nav.close_menu')} onClick={() => setDrawerOpen(false)}>
            ✕
          </button>
        </div>
        <div className="lang-switch" role="group" aria-label="Language">
          {LOCALES.map((l) => (
            <LangPill key={l} lang={l} current={locale} />
          ))}
        </div>
        <details>
          <summary>
            {t('nav.solutions')} <span>+</span>
          </summary>
          {solutions.map((s) => (
            <Link key={s.code} to={withLocale(`/solutions/${s.slug}`, locale)} onClick={() => setDrawerOpen(false)}>
              {s.code} — {s.name}
            </Link>
          ))}
          <Link to={withLocale('/solutions', locale)} onClick={() => setDrawerOpen(false)}>
            {t('nav.view_all_12')}
          </Link>
        </details>
        <details>
          <summary>
            {t('nav.industries')} <span>+</span>
          </summary>
          {industries.map((i) => (
            <Link key={i.slug} to={withLocale(`/industries/${i.slug}`, locale)} onClick={() => setDrawerOpen(false)}>
              {i.name}
            </Link>
          ))}
          <Link to={withLocale('/industries', locale)} onClick={() => setDrawerOpen(false)}>
            {t('nav.view_all_10')}
          </Link>
        </details>
        {STATIC_NAV_KEYS.map((key) => (
          <Link key={key} to={withLocale(STATIC_NAV_HREF[key], locale)} onClick={() => setDrawerOpen(false)}>
            {t(`nav.${key}`)}
          </Link>
        ))}
        <Link to={withLocale('/portal-login', locale)} onClick={() => setDrawerOpen(false)}>
          {t('nav.portal')}
        </Link>
        <Link to={withLocale('/meet-lisa', locale)} onClick={() => setDrawerOpen(false)}>
          Meet LISA
        </Link>
      </nav>
    </>
  );
}

function LangPill({ lang, current }: { lang: Locale; current: Locale }) {
  // Swaps the /:locale prefix of the current URL, preserving the rest of
  // the path so switching language never drops the visitor back to the
  // homepage — the same behavior the static build's per-locale URL trees
  // gave for free.
  const rest = window.location.pathname.replace(/^\/(en|fr|ar)/, '') || '/';
  return (
    <a href={`/${lang}${rest === '/' ? '' : rest}`} className={`lang-pill ${lang === current ? 'is-active' : ''}`} aria-current={lang === current ? 'true' : undefined} hrefLang={lang}>
      {LOCALE_LABEL[lang]}
    </a>
  );
}
